import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { User } from 'firebase/auth';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { firebaseAuth, registerLogout } from 'plugins/firebase';
import { getSession } from 'repositories/auth_repository';
import message from 'plugins/utils/message';
import i18n from 'plugins/i18n';

interface AuthState {
  usuario: User | null;
  loading: boolean;
}

interface LoginSessionResponse {
  error?: string;
  rutas?: unknown[];
  idioma?: string;
}

const initialState: AuthState = {
  usuario: null,
  loading: false,
};

export const loginWithEmailAndPassword = createAsyncThunk(
  'auth/loginWithEmailAndPassword',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        firebaseAuth,
        email.trim(),
        password,
      );
      const token = userCredential.user ? await userCredential.user.getIdToken() : null;
      const loginResponse = token
        ? ((await getSession(token)) as LoginSessionResponse | null)
        : null;
      if (!loginResponse || loginResponse.error) throw new Error(loginResponse?.error ?? 'Error de sesión');

      if (!loginResponse.rutas || loginResponse.rutas.length === 0) {
        throw new Error(
          'El usuario no tiene permisos para iniciar sesión en la Aplicación Web, contáctese con el administrador',
        );
      }

      const newLocale = loginResponse.idioma === 'es_ES' ? 'es' : 'de';
      localStorage.setItem('locale', newLocale);
      void i18n.changeLanguage(newLocale);
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
        message.error('Contraseña incorrecta');
      } else if (err?.code === 'auth/user-not-found') {
        message.error('El usuario no existe');
      } else if (err?.code === 'auth/invalid-email') {
        message.error('Correo electrónico inválido');
      } else {
        message.error(err?.message ?? 'Error desconocido');
      }
      return rejectWithValue(err?.message ?? 'Error al iniciar sesión');
    }
  },
);

export const asyncLogout = createAsyncThunk('auth/asyncLogout', async (_, { dispatch }) => {
  await firebaseAuth.signOut();
  registerLogout();
  dispatch(logoutAction());
});

export const recoveryPassword = createAsyncThunk(
  'auth/recoveryPassword',
  async (
    { email, onSendResult }: { email: string; onSendResult: (result: boolean) => void },
    { rejectWithValue },
  ) => {
    try {
      await sendPasswordResetEmail(firebaseAuth, email.trim());
      onSendResult(true);
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string };
      if (err?.code === 'auth/user-not-found') {
        message.error('El usuario no existe');
      } else if (err?.code === 'auth/invalid-email') {
        message.error('Correo electrónico inválido');
      } else {
        message.error(err?.message ?? 'Error desconocido');
      }
      onSendResult(false);
      return rejectWithValue(err?.message ?? 'Error al recuperar contraseña');
    }
  },
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginAction: (state, action: PayloadAction<User>) => {
      state.usuario = action.payload;
    },
    logoutAction: (state) => {
      state.usuario = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginWithEmailAndPassword.pending, (state) => {
        state.loading = true;
      })
      .addCase(loginWithEmailAndPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loginWithEmailAndPassword.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { loginAction, logoutAction } = authSlice.actions;
export default authSlice.reducer;
